import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AuthService } from '../../../../services/auth.service';
import { FunpayHelper, IFunpayDashboard, IFunpayTransaction } from '../../../../services/funpay.service';

/** How far back the transaction list looks, in days. */
type TransactionWindow = 7 | 30 | 90;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Adyen sends amounts in the currency's minor unit
 */
export function formatMinorUnits(value: number | undefined, currency: string): string {
    const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
    const decimals = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    return formatter.format((value ?? 0) / Math.pow(10, decimals));
}

@Component({
    selector: 'funpay-dashboard',
    templateUrl: './funpay-dashboard.html',
    imports: [
        DatePipe,
        FormsModule,
        RouterLink,
        TranslatePipe,
        ButtonModule,
        SelectButtonModule,
        SelectModule,
        SkeletonModule,
        TableModule,
        TagModule
    ]
})
export class FunPayDashboardComponent {
    id = input.required<string>();

    private funpay = inject(FunpayHelper);
    private auth = inject(AuthService);

    readonly formatMinorUnits = formatMinorUnits;

    loading = signal<boolean>(true);
    loadError = signal<string | null>(null);
    /** The server does not consider the account approved. Shown, never redirected, so it cannot loop with onboarding. */
    notLive = signal<boolean>(false);
    dashboard = signal<IFunpayDashboard | null>(null);

    selectedBalanceAccountId = signal<string>('');
    transactionWindow = signal<TransactionWindow>(30);
    transactions = signal<IFunpayTransaction[]>([]);
    nextCursor = signal<string | null>(null);
    transactionsLoading = signal<boolean>(false);
    transactionsError = signal<string | null>(null);

    readonly windowOptions: { labelKey: string; value: TransactionWindow }[] = [
        { labelKey: 'FUNPAY_WINDOW_7_DAYS', value: 7 },
        { labelKey: 'FUNPAY_WINDOW_30_DAYS', value: 30 },
        { labelKey: 'FUNPAY_WINDOW_90_DAYS', value: 90 }
    ];

    venueName = computed(() => {
        const venue = (this.auth.clientsInOrganization() ?? []).find((client) => client?.id === this.id());
        return venue?.name ?? '';
    });

    venuesLink = computed(() => ['/organizations', this.auth.organizationId(), 'funpay']);
    onboardingLink = computed(() => ['/organizations', this.auth.organizationId(), 'funpay', this.id(), 'onboarding']);

    verification = computed(() => this.dashboard()?.verification ?? null);

    selectedBalanceAccount = computed(() => {
        const accounts = this.dashboard()?.balanceAccounts ?? [];
        return accounts.find((account) => account.id === this.selectedBalanceAccountId()) ?? accounts[0] ?? null;
    });

    balanceAccountOptions = computed(() =>
        (this.dashboard()?.balanceAccounts ?? []).map((account) => ({
            label: account.description || account.id,
            value: account.id
        }))
    );

    /** One card per currency the selected balance account holds. */
    balances = computed(() => this.selectedBalanceAccount()?.balances ?? []);

    constructor() {
        effect(() => {
            const clientId = this.id();
            if (clientId) {
                untracked(() => void this.load(clientId));
            }
        });
    }

    async load(clientId: string): Promise<void> {
        this.loading.set(true);
        this.loadError.set(null);
        this.notLive.set(false);

        try {
            const result = await this.funpay.getDashboard(clientId);

            // Onboarding redirects here when it thinks the account is live
            if (result.error === 'FUNPAY_ACCOUNT_NOT_LIVE') {
                this.notLive.set(true);
                return;
            }

            if (result.error || !result.dashboard) {
                this.loadError.set(result.error ?? 'FAILED_TO_READ_FUNPAY_DASHBOARD');
                return;
            }

            this.dashboard.set(result.dashboard);
            this.selectedBalanceAccountId.set(result.dashboard.balanceAccounts[0]?.id ?? '');
        } finally {
            this.loading.set(false);
        }

        await this.loadTransactions(true);
    }

    selectBalanceAccount(balanceAccountId: string): void {
        this.selectedBalanceAccountId.set(balanceAccountId);
        void this.loadTransactions(true);
    }

    selectWindow(days: TransactionWindow): void {
        this.transactionWindow.set(days);
        void this.loadTransactions(true);
    }

    /** `reset` starts the list over; otherwise the next page is appended. */
    async loadTransactions(reset = false): Promise<void> {
        const balanceAccountId = this.selectedBalanceAccount()?.id;
        if (!balanceAccountId || this.transactionsLoading()) {
            return;
        }

        this.transactionsLoading.set(true);
        this.transactionsError.set(null);

        const to = new Date();
        const from = new Date(to.getTime() - this.transactionWindow() * DAY_MS);

        try {
            const result = await this.funpay.getTransactions(this.id(), {
                balanceAccountId,
                from,
                to,
                cursor: reset ? null : this.nextCursor()
            });

            if (result.error || !result.page) {
                this.transactionsError.set(result.error ?? 'FAILED_TO_LIST_FUNPAY_TRANSACTIONS');
                return;
            }

            this.transactions.set(reset ? result.page.transactions : [...this.transactions(), ...result.page.transactions]);
            this.nextCursor.set(result.page.nextCursor);
        } finally {
            this.transactionsLoading.set(false);
        }
    }

    /** Translation key for what kind of movement a transaction is. */
    categoryKey(transaction: IFunpayTransaction): string {
        switch (transaction.category) {
            case 'bank':
                return 'FUNPAY_CATEGORY_PAYOUT';
            case 'platformPayment':
                return 'FUNPAY_CATEGORY_PAYMENT';
            case 'internal':
                return 'FUNPAY_CATEGORY_INTERNAL';
            default:
                return transaction.amount.value >= 0 ? 'FUNPAY_CATEGORY_INCOMING' : 'FUNPAY_CATEGORY_OUTGOING';
        }
    }
}
